import axios from 'axios';

/**
 * Fetch a generated report PDF from the backend.
 *
 * The backend streams the file from S3 using server-side AWS credentials, so the
 * AWS access key/secret never reach the browser. Pass the `fileKey` returned by
 * the report-generation endpoints.
 *
 * @param {string} fileKey - The S3 object key returned by a generate-report endpoint.
 * @returns {Promise<Blob>} The report PDF as a Blob.
 */
export const fetchReportBlob = (fileKey) =>
  axios
    .get('/reports/download-report', { params: { key: fileKey }, responseType: 'blob' })
    .then((res) => res.data);

/**
 * Trigger a browser download for a Blob.
 *
 * Creating the link and revoking the object URL synchronously after `click()`
 * races the browser's asynchronous download and can save an empty/blank file
 * that opens as "about:blank". To avoid that we: force the PDF MIME type so the
 * OS treats the file correctly, append the link to the DOM (required by some
 * browsers), and defer cleanup until after the download has started.
 *
 * @param {Blob} blob - The binary file contents to download.
 * @param {string} filename - The name to save the file as (e.g. `report.pdf`).
 */
export const downloadBlob = (blob, filename) => {
  const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    link.remove();
    URL.revokeObjectURL(url);
  }, 0);
};
